/**
 * Markdown-lite renderer of help articles (W2-14 onboarding-wizard; contract HelpTopicDef.body "Markdown-lite body
 * message (no HTML)"). Blocks: paragraphs (separated by a blank line; single line breaks are kept), `- ` bullet
 * lists, `1. ` numbered lists, `| a | b |` tables (the first row is the header; `|---|` rows are separators) and
 * `### ` headings. Inline: **bold**, `code` and shortcuts. Everything else is plain text: no HTML is ever
 * interpreted.
 *
 * Shortcuts are never written as keys: `[[command]]` names a command, optionally with argument values
 * (`[[app.workspace workspace=publish]]`) and preferred chords (`[[segments.easing @KeyC]]`). It shows the key the
 * active keymap binds to it now, in parentheses ("(F4)", as key caps); when the command has no key it shows nothing
 * (one space before it goes too), so sentences are written to read well either way.
 */

import type { ReactNode } from 'react';
import type { Chord } from '../../app/registry/types';
import { Kbd } from '../../ui';
import type { KeyRef } from './keys';
import s from './Onboarding.module.css';

export type Inline =
  | { readonly kind: 'text' | 'bold' | 'code'; readonly text: string }
  | { readonly kind: 'key'; readonly text: string; readonly ref: KeyRef };

/** Resolves a shortcut reference to the chord bound now (null: none). */
export type KeyResolver = (ref: KeyRef) => Chord | null;

const KEY_TOKEN = /^\[\[([A-Za-z][\w.-]*)((?:\s+[^\s\]]+)*)\s*\]\]/;

function argValue(v: string): string | number | boolean {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v;
}

/** A `[[command k=v @Chord]]` reference (see the module header), or null when `src` does not hold one. */
export function parseKeyToken(src: string): { readonly ref: KeyRef; readonly length: number } | null {
  const match = KEY_TOKEN.exec(src);
  if (match === null) return null;
  const args: Record<string, string | number | boolean> = {};
  const prefer: Chord[] = [];
  for (const part of (match[2] ?? '').trim().split(/\s+/)) {
    if (part === '') continue;
    if (part.startsWith('@')) prefer.push(part.slice(1));
    else {
      const eq = part.indexOf('=');
      if (eq <= 0) return null;
      args[part.slice(0, eq)] = argValue(part.slice(eq + 1));
    }
  }
  const ref: KeyRef = {
    command: match[1] ?? '',
    ...(Object.keys(args).length > 0 ? { args } : {}),
    ...(prefer.length > 0 ? { prefer } : {}),
  };
  return { ref, length: match[0].length };
}

export type Block =
  | { readonly kind: 'paragraph'; readonly lines: readonly (readonly Inline[])[] }
  | { readonly kind: 'heading'; readonly text: readonly Inline[] }
  | { readonly kind: 'list'; readonly ordered: boolean; readonly items: readonly (readonly Inline[])[] }
  | {
      readonly kind: 'table';
      readonly head: readonly (readonly Inline[])[];
      readonly rows: readonly (readonly (readonly Inline[])[])[];
    };

/** Splits a line into text, **bold** and `code` runs. */
export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let i = 0;
  let plain = '';
  const flush = (): void => {
    if (plain !== '') out.push({ kind: 'text', text: plain });
    plain = '';
  };
  while (i < src.length) {
    if (src.startsWith('[[', i)) {
      const token = parseKeyToken(src.slice(i));
      if (token !== null) {
        flush();
        out.push({ kind: 'key', text: src.slice(i, i + token.length), ref: token.ref });
        i += token.length;
        continue;
      }
    }
    if (src.startsWith('**', i)) {
      const end = src.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({ kind: 'bold', text: src.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    if (src[i] === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ kind: 'code', text: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    plain += src[i] ?? '';
    i++;
  }
  flush();
  return out;
}

const TABLE_RULE = /^\|[\s:|-]+\|$/;
const BULLET = /^[-*]\s+/;
const NUMBERED = /^\d+[.)]\s+/;

function cells(row: string): Inline[][] {
  const inner = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((c) => parseInline(c.trim()));
}

/** Blocks of a Markdown-lite source. */
export function parseMarkdownLite(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? '').trimEnd();
    const trimmed = line.trim();
    if (trimmed === '') {
      i++;
      continue;
    }
    if (trimmed.startsWith('### ')) {
      blocks.push({ kind: 'heading', text: parseInline(trimmed.slice(4)) });
      i++;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const rows: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push((lines[i] ?? '').trim());
        i++;
      }
      const content = rows.filter((r) => !TABLE_RULE.test(r));
      const [head, ...body] = content;
      if (head !== undefined) blocks.push({ kind: 'table', head: cells(head), rows: body.map(cells) });
      continue;
    }
    if (BULLET.test(trimmed) || NUMBERED.test(trimmed)) {
      const ordered = NUMBERED.test(trimmed);
      const re = ordered ? NUMBERED : BULLET;
      const items: Inline[][] = [];
      while (i < lines.length && re.test((lines[i] ?? '').trim())) {
        items.push(parseInline((lines[i] ?? '').trim().replace(re, '')));
        i++;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    const para: Inline[][] = [];
    while (i < lines.length) {
      const l = (lines[i] ?? '').trim();
      if (l === '' || l.startsWith('|') || l.startsWith('### ') || BULLET.test(l) || NUMBERED.test(l)) break;
      para.push(parseInline(l));
      i++;
    }
    blocks.push({ kind: 'paragraph', lines: para });
  }
  return blocks;
}

/** Runs with shortcuts resolved: bound ones stay, unbound ones go together with one space before them. */
function resolveKeys(
  runs: readonly Inline[],
  keys: KeyResolver,
): Array<Inline | { readonly kind: 'chord'; readonly chord: Chord }> {
  const out: Array<Inline | { readonly kind: 'chord'; readonly chord: Chord }> = [];
  for (const r of runs) {
    if (r.kind !== 'key') {
      out.push(r);
      continue;
    }
    const chord = keys(r.ref);
    if (chord !== null) {
      out.push({ kind: 'chord', chord });
      continue;
    }
    const prev = out[out.length - 1];
    if (prev?.kind === 'text' && prev.text.endsWith(' ')) {
      out[out.length - 1] = { kind: 'text', text: prev.text.slice(0, -1) };
    }
  }
  return out;
}

const NO_KEYS: KeyResolver = () => null;

function renderInline(runs: readonly Inline[], keys: KeyResolver): ReactNode[] {
  return resolveKeys(runs, keys).map((r, k) => {
    switch (r.kind) {
      case 'bold':
        return <strong key={k}>{r.text}</strong>;
      case 'code':
        return <code key={k}>{r.text}</code>;
      case 'chord':
        return (
          <span key={k} className={s.keyRef}>
            (<Kbd chord={r.chord} />)
          </span>
        );
      default:
        return r.text;
    }
  });
}

/**
 * Plain text of a Markdown-lite source (search and excerpts); shortcuts read as "(label)" through `label` (the
 * keymap's text for a bound chord), unbound ones as nothing.
 */
export function plainText(
  src: string,
  keys: KeyResolver = NO_KEYS,
  label: (chord: Chord) => string = (c) => c,
): string {
  return parseMarkdownLite(src)
    .map((b) => {
      const join = (runs: readonly Inline[]): string =>
        resolveKeys(runs, keys)
          .map((r) => (r.kind === 'chord' ? `(${label(r.chord)})` : r.text))
          .join('');
      switch (b.kind) {
        case 'paragraph':
          return b.lines.map(join).join(' ');
        case 'heading':
          return join(b.text);
        case 'list':
          return b.items.map(join).join(' ');
        case 'table':
          return [b.head, ...b.rows].map((row) => row.map(join).join(' ')).join(' ');
      }
    })
    .join(' ');
}

export function MarkdownLite(props: { readonly source: string; readonly keys?: KeyResolver }) {
  const blocks = parseMarkdownLite(props.source);
  const keys = props.keys ?? NO_KEYS;
  return (
    <div className={s.article}>
      {blocks.map((b, k) => {
        switch (b.kind) {
          case 'heading':
            return <h4 key={k}>{renderInline(b.text, keys)}</h4>;
          case 'paragraph':
            return (
              <p key={k}>
                {b.lines.map((l, j) => (
                  <span key={j}>
                    {j > 0 && <br />}
                    {renderInline(l, keys)}
                  </span>
                ))}
              </p>
            );
          case 'list': {
            const items = b.items.map((it, j) => <li key={j}>{renderInline(it, keys)}</li>);
            return b.ordered ? <ol key={k}>{items}</ol> : <ul key={k}>{items}</ul>;
          }
          case 'table':
            return (
              <div key={k} className={s.tableWrap}>
                <table>
                  <thead>
                    <tr>
                      {b.head.map((c, j) => (
                        <th key={j} scope="col">
                          {renderInline(c, keys)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, j) => (
                      <tr key={j}>
                        {row.map((c, x) => (
                          <td key={x}>{renderInline(c, keys)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
        }
      })}
    </div>
  );
}
